import { ArrowDownIcon, ArrowUpIcon, ChevronsUpDownIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { InfoTooltip } from '@/components/atoms/InfoTooltip/InfoTooltip';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import { cn } from '@/lib/utils';

export type ToolRow = {
  name: string;
  enabled: boolean;
  /** Blocks the Enable switch while flipping it would permanently lose the tool. */
  toggleLocked?: boolean;
};

export type SortColumn = 'name' | 'enabled';

/** `null` = unsorted, i.e. the order the server reported its tools in. */
export type ToolsSort = { column: SortColumn; direction: 'asc' | 'desc' } | null;

type ToolsTableProps = {
  tools: ToolRow[];
  selectedName?: string;
  canWrite: boolean;
  emptyMessage: string;
  isLoading?: boolean;
  sort: ToolsSort;
  onSelect: (name: string) => void;
  onToggle: (name: string, enabled: boolean) => void;
  onSort: (column: SortColumn) => void;
};

const headerCellClasses =
  'sticky top-0 z-10 h-12 border-b border-b-stroke bg-background px-4 py-1 text-left align-middle';

const bodyCellClasses = 'h-14 border-b border-b-stroke px-4';

const SKELETON_ROW_INDICES = Array(5).fill(0);

type SortableHeaderProps = { label: string; direction?: 'asc' | 'desc'; onClick: () => void };

const ariaSort = (direction?: 'asc' | 'desc') => {
  if (direction === 'asc') {
    return 'ascending' as const;
  }
  if (direction === 'desc') {
    return 'descending' as const;
  }
  return 'none' as const;
};

const SortIcon = ({ direction }: { direction?: 'asc' | 'desc' }) => {
  if (direction === 'asc') {
    return <ArrowUpIcon className="size-4" aria-hidden="true" />;
  }
  if (direction === 'desc') {
    return <ArrowDownIcon className="size-4" aria-hidden="true" />;
  }
  return <ChevronsUpDownIcon className="size-4" aria-hidden="true" />;
};

/** The label names the button; the `aria-sort` on the parent `th` carries the direction. */
const SortableHeader = ({ label, direction, onClick }: SortableHeaderProps) => (
  <button
    type="button"
    onClick={onClick}
    className="flex items-center gap-1 rounded-sm text-sm leading-6 font-medium text-foreground-muted transition-colors hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:outline-none"
  >
    {label}
    <SortIcon direction={direction} />
  </button>
);

/**
 * Left pane of the MCP server page: every tool the server exposes, with a per-tool Enable
 * switch, and row click selecting the tool shown in the overview pane.
 *
 * The markup is a hand-rolled `<table>` rather than `@/components/ui/table`: that `TableHead`
 * hardcodes `border-y`, `px-2` and `first:rounded-l-md` / `last:rounded-r-md`, which give this
 * design a double top border and rounded header corners, and the `first:`/`last:` modifiers
 * survive tailwind-merge so `cn()` cannot undo them.
 */
const ToolsTable = ({
  tools,
  selectedName,
  canWrite,
  emptyMessage,
  isLoading,
  sort,
  onSelect,
  onToggle,
  onSort,
}: ToolsTableProps) => {
  const { t } = useTranslation();
  const directionOf = (column: SortColumn) =>
    sort?.column === column ? sort.direction : undefined;

  return (
    <div className="h-full min-w-0 flex-1 overflow-hidden rounded-lg border border-stroke bg-background">
      {/* `type="always"` because the design draws the bar permanently, not on hover (Radix's
          default). Dropping the transparent left border widens the thumb from 7px to the 8px
          the design specifies. */}
      <ScrollArea className="h-full" type="always" scrollBarClassName="border-l-0">
        <table className="w-full border-separate border-spacing-0">
          <thead>
            <tr>
              {/* `w-full` on the name header and `max-w-0` on its cells make the name column
                  take the leftover width and truncate, leaving Enable at content width. */}
              <th
                className={cn(headerCellClasses, 'w-full')}
                aria-sort={ariaSort(directionOf('name'))}
              >
                <SortableHeader
                  label={t('common.name')}
                  direction={directionOf('name')}
                  onClick={() => onSort('name')}
                />
              </th>
              <th className={headerCellClasses} aria-sort={ariaSort(directionOf('enabled'))}>
                <SortableHeader
                  label={t('common.enable')}
                  direction={directionOf('enabled')}
                  onClick={() => onSort('enabled')}
                />
              </th>
            </tr>
          </thead>
          <tbody>
            {isLoading &&
              SKELETON_ROW_INDICES.map((_, index) => (
                // eslint-disable-next-line react/no-array-index-key
                <tr key={`skeleton-row-${index}`}>
                  <td className={cn(bodyCellClasses, 'max-w-0')}>
                    <Skeleton className="h-4 w-full" />
                  </td>
                  <td className={bodyCellClasses}>
                    <Skeleton className="h-5 w-9" />
                  </td>
                </tr>
              ))}
            {!isLoading &&
              tools.map((tool) => (
                <tr
                  key={tool.name}
                  tabIndex={0}
                  aria-selected={tool.name === selectedName}
                  onClick={() => onSelect(tool.name)}
                  onKeyDown={(event) => {
                    // Only the row itself. The Enable switch is a button inside this row, so
                    // without this guard the row would `preventDefault` the very keystroke that
                    // activates it and the toggle would be mouse-only.
                    if (event.target !== event.currentTarget) {
                      return;
                    }
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      onSelect(tool.name);
                    }
                  }}
                  className={cn(
                    'cursor-pointer hover:bg-bg-muted-50',
                    'focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none focus-visible:ring-inset',
                    tool.name === selectedName && 'bg-bg-muted-50'
                  )}
                >
                  <td className={cn(bodyCellClasses, 'max-w-0')}>
                    <div className="truncate text-sm leading-none font-medium text-foreground">
                      {tool.name}
                    </div>
                  </td>
                  {/* Toggling must not also move the selection, so the click stops here. */}
                  <td className={bodyCellClasses} onClick={(event) => event.stopPropagation()}>
                    {/* The span carries the hover: a disabled switch emits no pointer events, so
                      the tooltip would never open on the switch itself. */}
                    <InfoTooltip
                      size="large"
                      content={tool.toggleLocked ? t('mcp.disable_locked_unreachable') : null}
                    >
                      <span className="inline-flex">
                        <Switch
                          checked={tool.enabled}
                          disabled={!canWrite || tool.toggleLocked}
                          aria-label={tool.name}
                          onCheckedChange={(checked) => onToggle(tool.name, checked)}
                        />
                      </span>
                    </InfoTooltip>
                  </td>
                </tr>
              ))}
            {!isLoading && tools.length === 0 && (
              <tr>
                <td
                  colSpan={2}
                  className="px-4 py-6 text-center text-sm leading-5 text-foreground-muted"
                >
                  {emptyMessage}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </ScrollArea>
    </div>
  );
};

export default ToolsTable;
